import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "./ui/sheet";

/**
 * Reusable mobile hamburger + slide-in nav drawer.
 *
 * `items`: [{ to, label, icon, testid }]
 * `header`: React node (brand block)
 * `footer`: React node (usually the user/logout block)
 * `triggerTestid`: data-testid for the hamburger button
 */
export default function MobileNavSheet({ items, header, footer, triggerTestid = "mobile-nav-trigger" }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid={triggerTestid}
          aria-label="Open menu"
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-[#E2E4E0] bg-white text-[#1A1D1A] hover:bg-[#F9F9F8] active:scale-95 transition"
        >
          <Menu size={20} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-[85%] max-w-xs p-0 bg-white flex flex-col [&>button.absolute]:hidden"
        data-testid="mobile-nav-drawer"
      >
        <div className="px-3 h-14 flex items-center justify-between gap-2 border-b border-[#E2E4E0]">
          {header && <div className="flex items-center gap-2 min-w-0">{header}</div>}
          <SheetClose asChild>
            <button
              data-testid="mobile-nav-close"
              aria-label="Close menu"
              className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-[#E2E4E0] bg-white text-[#1A1D1A] hover:bg-[#F9F9F8] active:scale-95 transition shrink-0"
            >
              <X size={20} />
            </button>
          </SheetClose>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`m-${item.testid}`}
              className={`flex items-center gap-3 px-3 min-h-[44px] rounded-md text-[15px] transition-colors ${
                location.pathname.startsWith(item.to)
                  ? "bg-[#E9F5EC] text-[#1B4332] font-medium"
                  : "text-[#1A1D1A] hover:bg-[#F9F9F8]"
              }`}
            >
              <item.icon size={18} strokeWidth={2} />
              {item.label}
            </Link>
          ))}
        </nav>
        {footer && <div className="border-t border-[#E2E4E0] p-3">{footer}</div>}
      </SheetContent>
    </Sheet>
  );
}
